import { useState, useEffect } from 'react';

export default function TextSphere() {
	const [rotation, setRotation] = useState({ x: 0, y: 0 });
	const [autoRotate, setAutoRotate] = useState(true);
	const [isDragging, setIsDragging] = useState(false);
	const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
	const [orbitAngle, setOrbitAngle] = useState(0);
	const [orbitScale, setOrbitScale] = useState(autoRotate ? 1 : 0);

	// Tech keywords to display in the sphere
	const words = [
		"AWS", "Bootstrap", "CSS", "Docker", "Expo", "Figma",
		"GitHub", "GSAP", "HTML", "Java", "Jenkins", "JS",
		"Kubernetes", "Linux", "MongoDB", "MySQL", "Node", "PHP",
		"Python", "React", "Redhat", "TailwindCSS", "Typescript"
	];

	useEffect(() => {
		let animationFrame;

		const animate = () => {
			if (autoRotate && !isDragging) {
				setRotation(prev => ({
					x: prev.x + 0.005,
					y: prev.y + 0.005
				}));

				// Update orbit animation
				setOrbitAngle(prev => (prev + 0.05) % (2 * Math.PI));
			}
			animationFrame = requestAnimationFrame(animate);
		};

		animate();
		return () => cancelAnimationFrame(animationFrame);
	}, [autoRotate, isDragging]);

	// Handle orbit scale animation when autoRotate changes
	useEffect(() => {
		if (autoRotate) {
			// Scale up animation
			const timer = setTimeout(() => setOrbitScale(1), 10);
			return () => clearTimeout(timer);
		} else {
			// Scale down animation
			setOrbitScale(0);
		}
	}, [autoRotate]);

	const handleMouseDown = (e) => {
		setIsDragging(true);
		setLastPosition({ x: e.clientX, y: e.clientY });
	};

	const handleMouseMove = (e) => {
		if (!isDragging) return;

		const deltaX = e.clientX - lastPosition.x;
		const deltaY = e.clientY - lastPosition.y;

		setRotation(prev => ({
			x: prev.x + deltaY * 0.005,
			y: prev.y + deltaX * 0.005
		}));

		setLastPosition({ x: e.clientX, y: e.clientY });
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const toggleAutoRotate = () => {
		setAutoRotate(!autoRotate);
	};

	// Calculate position of orbiting circle
	const orbitRadius = 20; // Size of the orbit
	const orbitX = Math.cos(orbitAngle) * orbitRadius;
	const orbitY = Math.sin(orbitAngle) * orbitRadius;

	return (
		<div className="flex flex-col items-center text-white">
			<div
				className="relative w-64 h-64 cursor-grab"
				onMouseDown={handleMouseDown}
				onMouseMove={handleMouseMove}
				onMouseUp={handleMouseUp}
				onMouseLeave={handleMouseUp}
			>
				{words.map((word, index) => {
					// Calculate position on the sphere
					const phi = Math.acos(-1 + (2 * index) / words.length);
					const theta = Math.sqrt(words.length * Math.PI) * phi;

					// Apply current rotation to the base position
					const radius = 150;

					// Base position
					let x = radius * Math.cos(theta) * Math.sin(phi);
					let y = radius * Math.sin(theta) * Math.sin(phi);
					let z = radius * Math.cos(phi);

					// Apply rotation matrix
					const cosX = Math.cos(rotation.x);
					const sinX = Math.sin(rotation.x);
					const cosY = Math.cos(rotation.y);
					const sinY = Math.sin(rotation.y);

					// Rotate around X axis
					const tempY1 = y * cosX - z * sinX;
					const tempZ1 = y * sinX + z * cosX;

					// Rotate around Y axis
					const tempX2 = x * cosY + tempZ1 * sinY;
					const tempZ2 = -x * sinY + tempZ1 * cosY;

					// Final rotated position
					const rotatedX = tempX2;
					const rotatedY = tempY1;
					const rotatedZ = tempZ2;

					// Calculate font size based on z position (closer = larger)
					const fontSize = Math.max(8, 22 * (rotatedZ + radius) / (2 * radius));
					const opacity = (rotatedZ + radius) / (2 * radius);

					return (
						<div
							key={index}
							className="absolute font-bai top-1/2 left-1/2 text-neon hover:text-cyan-300 transition-colors duration-300"
							style={{
								transform: `translate(-50%, -50%) translate3d(${rotatedX}px, ${rotatedY}px, ${rotatedZ}px)`,
								fontSize: `${fontSize}px`,
								opacity: opacity,
								textShadow: '0 0 5px rgba(0,0,0,0.5)',
								willChange: 'transform, opacity, fontSize',
								':hover': {
									textShadow: '0 0 10px #000, 0 0 20px #00fffc'
								}
							}}
							onMouseEnter={(e) => {
								e.target.style.textShadow = '0 0 10px #000, 0 0 20px #00fffc';
								e.target.style.color = '#00fffc';
							}}
							onMouseLeave={(e) => {
								e.target.style.textShadow = '0 0 5px rgba(0,0,0,0.5)';
								e.target.style.color = '#00fffc'; // back to blue-400
							}}
						>
							{word}
							{/* <img src={`./tech_imgs/${word}.png`} alt={word} className='w-16' /> */}
						</div>
					);
				})}
			</div>

			<div className="mt-24">
				{/* Horizontal Toggle Switch with Simple Orbit */}
				<div className="flex items-center space-x-3">
					<span className="text-sm text-gray-400">Orbit</span>

					{/* Toggle container */}
					<div
						className="relative w-16 h-8 bg-gray-700 rounded-full flex items-center px-1 cursor-pointer transition-all duration-300"
						onClick={toggleAutoRotate}
					>
						{/* Toggle knob */}
						<div
							className={`relative h-6 w-6 rounded-full shadow-lg z-10 flex items-center justify-center transition-all duration-500 ${autoRotate ? 'transform translate-x-8 bg-yellow-500' : 'bg-gray-500'
								}`}
						>
							{/* Just the circle center */}
							<div className={`w-4 h-4 rounded-full ${autoRotate ? 'bg-yellow-300' : 'bg-white'}`}></div>

							{/* The orbit line that scales out */}
							<div
								className="absolute rounded-full border border-white transition-all duration-500"
								style={{
									width: `${10 + 40 * orbitScale}px`,
									height: `${10 + 40 * orbitScale}px`,
									opacity: 0.7 * orbitScale,
									transform: `scale(${orbitScale})`,
									transformOrigin: 'center'
								}}
							></div>

							{/* The single orbiting circle */}
							{autoRotate && (
								<div
									className="absolute w-2 h-2 bg-[#00fffc] rounded-full shadow-lg z-20"
									style={{
										transform: `translate(${orbitX * 1.2}px, ${orbitY * 1.2}px)`,
										opacity: orbitScale,
										transition: 'opacity 0.5s ease-out'
									}}
								></div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}